/**
 * O fluxo Authorization Code + PKCE, de ponta a ponta (H01, ADR-0006).
 *
 * ── Os quatro momentos ────────────────────────────────────────────────────
 *
 *   1. `iniciarLogin`      — sai da aplicação para o provedor
 *   2. `concluirRetorno`   — volta com o código e troca por token
 *   3. `renovar`           — mantém a sessão viva sem o usuário perceber
 *   4. `sair`              — encerra no provedor, não só aqui
 *
 * ── A renovação tem dois caminhos, e a ordem importa ──────────────────────
 *
 * Com refresh token em mãos (sessão em andamento), o caminho é o `refresh_token`
 * grant: uma requisição, sem iframe, sem depender de cookie.
 *
 * Sem ele — recarregou a página, e o cofre é só memória —, o caminho é o
 * `prompt=none` num iframe escondido: o cookie de SSO do Keycloak ainda está no
 * navegador, então o provedor devolve um código novo sem pedir senha. É o que
 * paga o preço de não persistir token.
 *
 * O iframe funciona porque o provedor e a aplicação compartilham o site
 * registrável (`auth.cliqcrm.com.br` dentro de `app.cliqcrm.com.br`; em
 * desenvolvimento, duas portas de `localhost`). Se um dia o provedor mudar de
 * domínio, este caminho para de funcionar em silêncio — e é por isso que o
 * `reautenticarEmPopup` existe como rede de baixo.
 *
 * ── Expirar não pode destruir o que a pessoa escreveu ─────────────────────
 *
 * A H01 nomeia isto como *"o caso mais maltratado"*. A defesa aqui é
 * estrutural, não um salvamento de rascunho: **a aplicação nunca é
 * descarregada**. Silêncio primeiro, popup depois — nos dois a aba continua
 * montada, e a resposta digitada pela metade continua no estado do React onde
 * sempre esteve. Redirecionamento de página inteira só acontece no login
 * inicial, quando não há nada a perder.
 */

import { guardar, ler, limpar, type Credencial, atrasoAteRenovar, venceu } from './cofre';
import { descobrir, type Descoberta } from './descoberta';
import { destinoSeguro, prepararPedido, iguaisEmTempoConstante } from './pkce';
import { configuracaoDaInterface } from '@/shared/config/configuracao';
import { REALM_DE_IDENTIDADE } from './revenda';

/**
 * O pedido em curso, atravessando o redirecionamento.
 *
 * Aqui `sessionStorage` é inevitável: o navegador sai da página e volta, e a
 * memória do módulo morre no caminho. É aceitável porque o verificador é de USO
 * ÚNICO e não vale nada sozinho — sem o código de autorização correspondente,
 * que chega pela URL de retorno e é consumido na hora, ele não abre nada. É o
 * oposto do token, que vale por quinze minutos para qualquer requisição.
 *
 * Apagado no primeiro instante da volta, dê certo ou dê errado.
 */
const CHAVE_DO_PEDIDO = 'cliqcrm.pedido-oidc';

export interface ConfiguracaoOidc {
  readonly baseDoProvedor: string;
  readonly clientId: string;
  readonly redirectUri: string;
}

/**
 * O client daquela revenda, dentro do realm de identidade.
 *
 * `null` é o PRIMEIRO passo do login: o client genérico, que não carimba
 * revenda nenhuma. O token que ele emite prova quem a pessoa é e serve uma
 * rota só — a que lista em quais revendas ela entra.
 *
 * Com revenda, é o client dela: mesmo realm, mesma sessão no provedor, e um
 * mapper de valor fixo que carimba o `tenant_id`. É por isso que escolher a
 * revenda não pede senha de novo.
 */
export function clientDaRevenda(base: string, revenda: string | null): string {
  return revenda === null ? base : `${base}-${revenda}`;
}

export function configuracaoOidc(revenda: string | null = null): ConfiguracaoOidc {
  // Vem de `/config.js`, e não de `import.meta.env`: variável do Vite é
  // embutida no build, e o mesmo artefato precisa servir os três ambientes.
  // Ver o cabeçalho de `configuracao.ts`.
  const { oidcBase, oidcClient } = configuracaoDaInterface();

  return {
    baseDoProvedor: oidcBase,
    clientId: clientDaRevenda(oidcClient, revenda),
    // A origem é a da própria página, sempre. Configurá-la seria convite a
    // apontar para outro lugar — e `redirect_uri` divergente é recusado pelo
    // realm de qualquer forma, com uma mensagem que não ajuda ninguém.
    redirectUri: `${location.origin}/retorno`,
  };
}

/**
 * Os endpoints do provedor. Um realm só, para todas as revendas.
 *
 * A revenda deixou de escolher o realm e passou a escolher o client — ver
 * `clientDaRevenda`. Por isso a descoberta não recebe mais parâmetro: qualquer
 * um aqui seria ignorado, e recebe-lo sugeriria uma escolha que não existe
 * mais.
 */
function descobertaDoProvedor(): Promise<Descoberta> {
  return descobrir(configuracaoOidc().baseDoProvedor, REALM_DE_IDENTIDADE);
}

interface PedidoGuardado {
  readonly verificador: string;
  readonly estado: string;
  readonly nonce: string;
  readonly destino: string;
  /** `null` no primeiro passo — a revenda ainda vai ser escolhida. */
  readonly revenda: string | null;
}

function urlDeAutorizacao(
  descoberta: Descoberta,
  configuracao: ConfiguracaoOidc,
  pedido: PedidoGuardado,
  desafio: string,
  extras: Record<string, string> = {},
): string {
  const url = new URL(descoberta.authorization_endpoint);
  url.searchParams.set('client_id', configuracao.clientId);
  url.searchParams.set('redirect_uri', configuracao.redirectUri);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid profile email');
  url.searchParams.set('state', pedido.estado);
  url.searchParams.set('nonce', pedido.nonce);
  url.searchParams.set('code_challenge', desafio);
  url.searchParams.set('code_challenge_method', 'S256');
  for (const [chave, valor] of Object.entries(extras)) url.searchParams.set(chave, valor);
  return url.toString();
}

/**
 * Manda o usuário para o provedor.
 *
 * `acao` carrega um `kc_action` do Keycloak. O uso previsto é
 * `CONFIGURE_TOTP`: quando o tenant exige MFA (B15) e a pessoa ainda não tem
 * segundo fator, ela precisa CADASTRAR, não só desafiar. Sem isto, quem ligou a
 * exigência veria os usuários batendo num login que recusa sem dizer o que
 * fazer.
 */
export async function iniciarLogin(
  revenda: string | null,
  destino: string = location.pathname + location.search,
  acao?: 'CONFIGURE_TOTP',
): Promise<void> {
  const configuracao = configuracaoOidc(revenda);
  const descoberta = await descobertaDoProvedor();
  const { pedido, desafio } = await prepararPedido(destino);

  const guardado: PedidoGuardado = { ...pedido, revenda };
  sessionStorage.setItem(CHAVE_DO_PEDIDO, JSON.stringify(guardado));

  location.assign(
    urlDeAutorizacao(descoberta, configuracao, guardado, desafio, acao ? { kc_action: acao } : {}),
  );
}

export class RetornoInvalidoError extends Error {
  constructor(motivo: string) {
    super(motivo);
    this.name = 'RetornoInvalidoError';
  }
}

/**
 * Lê o retorno do provedor e devolve o pedido correspondente.
 *
 * Separada da troca por token porque é a parte que dá para provar sem rede: é
 * aqui que mora a verificação de `state`, que é o que impede um código de
 * outra pessoa de virar sessão nesta aba.
 */
export function conferirRetorno(
  parametros: URLSearchParams,
  guardadoBruto: string | null,
): {
  codigo: string;
  pedido: PedidoGuardado;
} {
  const erro = parametros.get('error');
  if (erro) {
    // `login_required` é o "não" esperado do `prompt=none` e não é falha: quem
    // chama trata como "não há sessão", não como erro para mostrar na tela.
    throw new RetornoInvalidoError(parametros.get('error_description') ?? erro);
  }

  if (!guardadoBruto) {
    throw new RetornoInvalidoError(
      'O pedido que começou este login não foi encontrado. Ele pode ter sido feito em outra aba.',
    );
  }

  let pedido: PedidoGuardado;
  try {
    pedido = JSON.parse(guardadoBruto) as PedidoGuardado;
  } catch {
    throw new RetornoInvalidoError('O pedido guardado está corrompido.');
  }

  const estado = parametros.get('state');
  if (!estado || !iguaisEmTempoConstante(estado, pedido.estado)) {
    // O `state` não bate: ou o retorno veio de outro fluxo, ou alguém induziu
    // esta aba a processar um código que não é dela. Nos dois casos a resposta
    // é a mesma — recusar sem tentar adivinhar qual foi.
    throw new RetornoInvalidoError('O `state` do retorno não confere com o do pedido.');
  }

  const codigo = parametros.get('code');
  if (!codigo) throw new RetornoInvalidoError('O provedor voltou sem código de autorização.');

  return { codigo, pedido };
}

interface RespostaDeToken {
  access_token?: unknown;
  refresh_token?: unknown;
  id_token?: unknown;
  expires_in?: unknown;
}

/**
 * Monta a credencial a partir da resposta do provedor.
 *
 * `expires_in` vira instante absoluto aqui, e não em quem consome: duração
 * relativa guardada e comparada depois erra pelo tempo que passou entre uma
 * coisa e outra, que é justamente o tempo da requisição.
 */
export function interpretarToken(bruto: unknown, agora = Date.now()): Credencial {
  const resposta = (bruto ?? {}) as RespostaDeToken;
  const acesso = resposta.access_token;
  if (typeof acesso !== 'string' || acesso === '') {
    throw new RetornoInvalidoError('O provedor respondeu sem `access_token`.');
  }

  const segundos = typeof resposta.expires_in === 'number' ? resposta.expires_in : 900;

  return {
    acesso,
    expiraEm: agora + segundos * 1000,
    refresh: typeof resposta.refresh_token === 'string' ? resposta.refresh_token : null,
    idToken: typeof resposta.id_token === 'string' ? resposta.id_token : null,
    amr: amrDe(acesso),
  };
}

/**
 * Lê o `amr` do access token, sem verificar assinatura.
 *
 * A verificação é do servidor (B14) e repeti-la aqui não acrescentaria nada:
 * quem consegue forjar o token também consegue forjar a resposta da API. O uso
 * deste valor é EXCLUSIVAMENTE de interface — mostrar "entrou com segundo
 * fator" e decidir se oferece o cadastro de MFA. Nenhuma autorização depende
 * dele, o que é a regra da H01: *"esconder botão não é autorização"*.
 */
function amrDe(acesso: string): readonly string[] {
  const corpo = acesso.split('.')[1];
  if (!corpo) return [];
  try {
    const json = atob(corpo.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { amr?: unknown };
    return Array.isArray(claims.amr)
      ? claims.amr.filter((v): v is string => typeof v === 'string')
      : [];
  } catch {
    return [];
  }
}

/** Para quem o id token foi emitido (`azp`), sem verificar assinatura: só monta a URL do logout. */
function clientDoToken(idToken: string | null | undefined): string | null {
  const corpo = idToken?.split('.')[1];
  if (!corpo) return null;
  try {
    const claims = JSON.parse(atob(corpo.replace(/-/g, '+').replace(/_/g, '/'))) as {
      azp?: unknown;
    };
    return typeof claims.azp === 'string' && claims.azp ? claims.azp : null;
  } catch {
    return null;
  }
}

async function trocarNoProvedor(
  descoberta: Descoberta,
  corpo: URLSearchParams,
): Promise<Credencial> {
  const resposta = await fetch(descoberta.token_endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: corpo,
  });

  if (!resposta.ok) {
    const detalhe = await resposta.text().catch(() => '');
    throw new RetornoInvalidoError(
      `O provedor recusou a troca (HTTP ${resposta.status}). ${detalhe.slice(0, 200)}`,
    );
  }

  return interpretarToken(await resposta.json());
}

/** Fecha o fluxo: valida a volta, troca o código e guarda. Devolve o destino. */
/** O que a volta do provedor produziu: para onde ir, e de quem é a sessão. */
export interface RetornoConcluido {
  readonly destino: string;
  /**
   * A revenda do PEDIDO, e não a que o endereço sugere.
   *
   * É ela que o provedor de sessão precisa para chamar `/v1/me` — e ela vem do
   * pedido guardado, que é o único lugar que sabe com qual client este login
   * foi feito.
   *
   * `null` é a volta do PRIMEIRO passo: a pessoa provou quem é e ainda não
   * escolheu onde entrar. O token dessa volta não carrega `tenant_id`, então
   * `/v1/me` recusaria — quem ele serve é `/v1/entrada/organizacoes`.
   */
  readonly revenda: string | null;
}

export async function concluirRetorno(parametros: URLSearchParams): Promise<RetornoConcluido> {
  const guardadoBruto = sessionStorage.getItem(CHAVE_DO_PEDIDO);
  // Apagado ANTES de qualquer await: se a troca falhar e o usuário recarregar,
  // um pedido velho não pode ser reaproveitado com um código novo.
  sessionStorage.removeItem(CHAVE_DO_PEDIDO);

  const { codigo, pedido } = conferirRetorno(parametros, guardadoBruto);
  const configuracao = configuracaoOidc(pedido.revenda);
  const descoberta = await descobertaDoProvedor();

  const credencial = await trocarNoProvedor(
    descoberta,
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: configuracao.clientId,
      redirect_uri: configuracao.redirectUri,
      code: codigo,
      code_verifier: pedido.verificador,
    }),
  );

  guardar(credencial);
  return { destino: destinoSeguro(pedido.destino), revenda: pedido.revenda };
}

/** Renova pelo refresh token. Devolve `false` quando não há como. */
/**
 * Uma renovação de cada vez, para a aplicação inteira.
 *
 * ── O que acontecia sem isto ──────────────────────────────────────────────
 *
 * A trava existia, mas só em `shared/api/cliente.ts` — e cobria apenas as renovações
 * que nascem de uma requisição. O provedor de sessão chama a renovação por
 * outros dois caminhos (a primeira carga e o temporizador de fundo), e esses
 * passavam por fora.
 *
 * Os dois lados então competiam pelo MESMO pedido no `sessionStorage`, que tem
 * chave única. Cada renovação sobrescrevia o `state` da anterior, e a
 * conferência de retorno recusava todas menos a última.
 *
 * O sintoma, medido no ambiente compartilhado: **42 pedidos de autorização**
 * para 15 chamadas de `/v1/me`. Não era erro — era a tela piscando entre a
 * entrada e a aplicação até que uma das tentativas sobrevivesse.
 *
 * ── Por que aqui, e não em quem chama ────────────────────────────────────
 *
 * Porque uma trava por chamador protege só aquele chamador. Aqui, ela vale para
 * qualquer caminho que apareça depois — e vai aparecer: já são três.
 *
 * ── E por que a ordem importa ────────────────────────────────────────────
 *
 * `refresh` antes de `silêncio` porque ele é uma requisição, sem iframe e sem
 * depender de cookie de terceiro. O iframe é o caminho de quando não há refresh
 * — ou quando ele já foi gasto.
 *
 * Com rotação de refresh ligada (`refreshTokenMaxReuse: 0`), duas renovações
 * simultâneas fariam a segunda usar um refresh que a primeira já invalidou — e
 * o Keycloak derruba a sessão inteira, entendendo reuso como vazamento. A trava
 * é o que impede isso.
 */
let renovacaoEmCurso: Promise<boolean> | null = null;

export function renovarSessao(revenda: string | null): Promise<boolean> {
  renovacaoEmCurso ??= (async () => {
    try {
      return (await renovarComRefresh(revenda)) || (await renovarEmSilencio(revenda));
    } finally {
      renovacaoEmCurso = null;
    }
  })();
  return renovacaoEmCurso;
}

export async function renovarComRefresh(revenda: string | null): Promise<boolean> {
  const atual = ler();
  if (!atual?.refresh) return false;

  const configuracao = configuracaoOidc(revenda);
  const descoberta = await descobertaDoProvedor();

  try {
    guardar(
      await trocarNoProvedor(
        descoberta,
        new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: configuracao.clientId,
          refresh_token: atual.refresh,
        }),
      ),
    );
    return true;
  } catch {
    // Refresh recusado é o esperado quando a sessão do provedor acabou. Não é
    // erro para mostrar: o passo seguinte é tentar o silêncio, e depois pedir
    // login. Estourar aqui transformaria fim de sessão em tela de erro.
    return false;
  }
}

/**
 * Fecha o fluxo que aconteceu num iframe ou num popup.
 *
 * A troca do código é feita AQUI, na aba principal, e não dentro do quadro
 * embutido: o verificador PKCE está na memória desta aba, e mandá-lo para lá
 * seria expor o segredo do fluxo para economizar uma mensagem.
 *
 * Devolve booleano em vez de estourar porque nenhum destes caminhos é erro para
 * mostrar: os dois têm um passo seguinte — o silêncio cai no popup, o popup cai
 * no redirecionamento.
 */
async function concluirEmbutido(
  descoberta: Descoberta,
  configuracao: ConfiguracaoOidc,
  guardado: PedidoGuardado,
  parametros: URLSearchParams | null,
): Promise<boolean> {
  if (!parametros) return false;

  try {
    const { codigo } = conferirRetorno(parametros, JSON.stringify(guardado));
    guardar(
      await trocarNoProvedor(
        descoberta,
        new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: configuracao.clientId,
          redirect_uri: configuracao.redirectUri,
          code: codigo,
          code_verifier: guardado.verificador,
        }),
      ),
    );
    return true;
  } catch {
    return false;
  }
}

const TIMEOUT_DO_SILENCIO_MS = 8_000;

/**
 * Recupera a sessão sem o usuário ver: `prompt=none` num iframe escondido.
 *
 * O provedor responde de duas formas, e as duas são normais: com um código,
 * quando o cookie de SSO está lá; ou com `error=login_required`, quando não
 * está. A segunda NÃO é falha — é a resposta de "esta pessoa precisa logar".
 */
export async function renovarEmSilencio(revenda: string | null): Promise<boolean> {
  const configuracao = configuracaoOidc(revenda);
  const descoberta = await descobertaDoProvedor();
  const { pedido, desafio } = await prepararPedido('/');
  const guardado: PedidoGuardado = { ...pedido, revenda };

  const url = urlDeAutorizacao(descoberta, configuracao, guardado, desafio, { prompt: 'none' });

  const parametros = await new Promise<URLSearchParams | null>((resolver) => {
    const quadro = document.createElement('iframe');
    quadro.style.display = 'none';
    quadro.setAttribute('aria-hidden', 'true');
    quadro.title = 'renovação de sessão';

    // Timeout obrigatório: se o provedor não responder, a promessa ficaria
    // pendente para sempre e a aplicação nunca sairia de "carregando".
    const relogio = setTimeout(() => encerrar(null), TIMEOUT_DO_SILENCIO_MS);

    function encerrar(resultado: URLSearchParams | null): void {
      clearTimeout(relogio);
      window.removeEventListener('message', aoReceber);
      quadro.remove();
      resolver(resultado);
    }

    function aoReceber(evento: MessageEvent<unknown>): void {
      // Só a própria origem: a página de retorno dentro do iframe é nossa, e
      // aceitar mensagem de qualquer origem deixaria qualquer site postar um
      // "código" para esta aba.
      if (evento.origin !== location.origin) return;
      const dado = evento.data as { tipo?: unknown; busca?: unknown } | null;
      if (!dado || dado.tipo !== 'cliqcrm:retorno-oidc' || typeof dado.busca !== 'string') return;
      encerrar(new URLSearchParams(dado.busca));
    }

    window.addEventListener('message', aoReceber);
    quadro.src = url;
    document.body.appendChild(quadro);
  });

  return concluirEmbutido(descoberta, configuracao, guardado, parametros);
}

/**
 * Última tentativa antes do redirecionamento: login numa janela separada.
 *
 * A aba principal continua montada, então o que a pessoa estava escrevendo
 * continua no estado do React. É a diferença entre "entre de novo" e "entre de
 * novo e redigite a resposta que você tinha acabado de escrever".
 *
 * Devolve `false` se o navegador bloquear a janela — aí quem chama decide, e a
 * decisão certa é avisar antes de redirecionar, não redirecionar.
 */
export async function reautenticarEmPopup(
  revenda: string,
  extras: Record<string, string> = {},
): Promise<boolean> {
  // O client da revenda, como na renovação: o client sem revenda emite o
  // token da tela de entrada, que não abre organização, e a API o recusa com
  // 401 em qualquer outra rota — o step-up inteiro caía nisso.
  const configuracao = configuracaoOidc(revenda);
  const descoberta = await descobertaDoProvedor();
  const { pedido, desafio } = await prepararPedido('/');
  const guardado: PedidoGuardado = { ...pedido, revenda };

  const url = urlDeAutorizacao(descoberta, configuracao, guardado, desafio, extras);
  const janela = window.open(url, 'cliqcrm-login', 'width=520,height=680');
  if (!janela) return false;

  const parametros = await new Promise<URLSearchParams | null>((resolver) => {
    const aberta = janela;
    const relogio = setInterval(() => {
      // Fechou a janela sem concluir: é desistência, e a promessa precisa
      // resolver mesmo assim — senão a aplicação fica esperando para sempre.
      if (aberta.closed) encerrar(null);
    }, 400);

    function encerrar(resultado: URLSearchParams | null): void {
      clearInterval(relogio);
      window.removeEventListener('message', aoReceber);
      resolver(resultado);
    }

    function aoReceber(evento: MessageEvent<unknown>): void {
      if (evento.origin !== location.origin) return;
      const dado = evento.data as { tipo?: unknown; busca?: unknown } | null;
      if (!dado || dado.tipo !== 'cliqcrm:retorno-oidc' || typeof dado.busca !== 'string') return;
      aberta.close();
      encerrar(new URLSearchParams(dado.busca));
    }

    window.addEventListener('message', aoReceber);
  });

  return concluirEmbutido(descoberta, configuracao, guardado, parametros);
}

/**
 * Sai de verdade: encerra no provedor, não só aqui.
 *
 * Limpar o cofre e mostrar a tela de login pareceria logout e não seria: o
 * cookie de SSO continuaria válido, e o próximo "entrar" — inclusive o de outra
 * pessoa no mesmo computador — entraria direto, sem senha. É o requisito
 * *"logout revogando no provedor"* da H01.
 *
 * O cofre é limpo ANTES do redirecionamento, para o intervalo entre uma coisa e
 * outra não deixar a interface autenticada.
 */
export async function sair(revenda: string | null): Promise<void> {
  const atual = ler();
  limpar();

  const configuracao = configuracaoOidc(revenda);
  const descoberta = await descobertaDoProvedor();

  /*
   * O client é o do TOKEN, e não o da revenda lembrada (28/10). "Trocar de
   * organização" esquece a revenda e deixa o token dela no cofre; na tela de
   * escolher, o "Entrar com outra conta" mandava `client_id=<base>` com um
   * `id_token_hint` emitido para `<base>-<revenda>`, e o provedor parava em
   * "Parâmetro inválido: id_token_hint".
   */
  const url = new URL(descoberta.end_session_endpoint);
  url.searchParams.set('client_id', clientDoToken(atual?.idToken) ?? configuracao.clientId);
  url.searchParams.set('post_logout_redirect_uri', location.origin);
  if (atual?.idToken) url.searchParams.set('id_token_hint', atual.idToken);

  location.assign(url.toString());
}

export { atrasoAteRenovar, venceu };

/**
 * Reautentica de VERDADE, para o step-up (B17, H13).
 *
 * ── Por que `reautenticarEmPopup` sozinha não serve ───────────────────────
 *
 * Ela abre o provedor sem `prompt`, e o cookie de SSO do Keycloak responde na
 * hora: a pessoa vê a janela piscar e voltar. É o comportamento certo para
 * renovar sessão expirada — e o errado para step-up, porque o `auth_time` do
 * token novo continua sendo o do login da manhã.
 *
 * O servidor confere exatamente isso e recusaria a concessão. A pessoa veria
 * uma janela abrir, fechar, e um erro dizendo para confirmar a identidade que
 * ela acabou de confirmar.
 *
 * `prompt=login` obriga o provedor a pedir a senha de novo mesmo com SSO
 * válido, e é o que faz o `auth_time` avançar.
 */
export async function reautenticarParaStepUp(revenda: string): Promise<boolean> {
  return reautenticarEmPopup(revenda, { prompt: 'login' });
}
